import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ChangeHistory } from "@/common/components/ChangeHistory";
import { ResentContactEmailModal } from "@/common/components/ResentContactEmailModal";
import { AddressSection } from "@/features/privateCompanyLeadsFunnel/components/AddressSection";
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist";
import { ContactsSection } from "@/features/privateCompanyLeadsFunnel/components/ContactsSection";
import { GeneralInformationSection } from "@/features/privateCompanyLeadsFunnel/components/GeneralInformationSection";
import { GenericDocumentsSection } from "@/features/privateCompanyLeadsFunnel/components/GenericDocumentsSection";
import { LeadHeader } from "@/features/privateCompanyLeadsFunnel/components/LeadHeader";
import { OperationalInformationSection } from "@/features/privateCompanyLeadsFunnel/components/OperationalInformationSection";
import { ResponsibleSection } from "@/features/privateCompanyLeadsFunnel/components/ResponsibleSection";
import { stateStrategy, translateStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant";
import useAdvancePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/useAdvancePrivateCompanyLead";
import usePrivateCompanyLeadForm from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLeadForm";
import { LeadFormStateProps } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import useReviewSectionLead from "../../hooks/useReviewSectionLead";
import { SpecialFees } from "../SpecialFees";

const LeadQuotingManagementReviewForm = ({ id, privateCompanyLead }: LeadFormStateProps) => {
  const queryClient = useQueryClient();

  const {
    serverError,
    formMethods,
    redirectOnSuccess,
    onError,
    passedEveryRequirement,
    submit,
    submitManagers,
    updatePrivateCompanyLeadManagersIsPending,
  } = usePrivateCompanyLeadForm({ privateCompanyLead });

  const {
    control,
    register,
    formState: { errors },
  } = formMethods;

  const isInQuotingManagementReview = useMemo(
    () => privateCompanyLead.state === 'quoting_management_review', [
    privateCompanyLead.state]
  );

  const isInQuotingClientReview = useMemo(
    () => privateCompanyLead.state === 'quoting_client_review',
    [privateCompanyLead.state]
  );

  const {
    mutate: advancePrivateCompanyLead,
    isPending: advancePrivateCompanyLeadIsPending
  } = useAdvancePrivateCompanyLead({
    id,
    onSuccess: () => redirectOnSuccess('/private-clients'),
    onError
  });

  const { mutate: approveSpecialFeesInformationLead } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'approved',
    section: 'fee_rule_attributes',
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', id] }),
    onError,
  });

  const { mutate: declineSpecialFeesInformationLead } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'disapproved',
    section: 'fee_rule_attributes',
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', id] }),
    onError,
  });

  return (
    <FormProvider {...formMethods}>
      <form>
        <LeadHeader
          privateCompanyLeadId={id}
          display_name={privateCompanyLead.display_name}
          url={privateCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={privateCompanyLead.archived_at}
          buttons={
            <>
              {
                isInQuotingClientReview && !passedEveryRequirement ?
                  <ResentContactEmailModal contacts={privateCompanyLead.contacts || []} /> :
                  <Button
                    type="button"
                    className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                    disabled={advancePrivateCompanyLeadIsPending || isInQuotingClientReview || !passedEveryRequirement}
                    onClick={() => advancePrivateCompanyLead()}
                    isLoading={advancePrivateCompanyLeadIsPending}
                  >
                    {(isInQuotingManagementReview || isInQuotingClientReview) ? 'Enviar para Contatos' : 'Enviar para análise do Gestor'}
                  </Button>
              }
            </>
          }
        />
        <div className='flex gap-6'>
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, tente novamente</p>}
            <ResponsibleSection
              privateCompanyLead={privateCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadManagersIsPending}
            />
            <OperationalInformationSection
              privateCompanyLeadId={id}
              operational_information={privateCompanyLead?.operational_information}
              financial_information={privateCompanyLead?.financial_information}
              contract={privateCompanyLead?.contract}
              control={control}
              register={register}
              errors={errors}
              disabled
            />
            <SpecialFees
              control={control}
              errors={errors}
              fee_rule={privateCompanyLead.fee_rule}
              approveSpecialFeesInformationLead={
                !isInQuotingClientReview
                  ? approveSpecialFeesInformationLead
                  : undefined
              }
              declineSpecialFeesInformationLead={
                !isInQuotingClientReview
                  ? declineSpecialFeesInformationLead
                  : undefined
              }
              disabled
            />
            <GeneralInformationSection
              general_information={privateCompanyLead?.general_information}
              register={register}
              errors={errors}
              disabled
            />
            <AddressSection
              address={privateCompanyLead?.address}
              errors={errors}
              disabled
            />
            <ContactsSection
              privateCompanyLeadId={id}
              leadState={privateCompanyLead?.state}
              contacts={privateCompanyLead?.contacts}
              contact_for_master_user={privateCompanyLead?.contact_for_master_user}
              control={control}
            />
            <GenericDocumentsSection
              privateCompanyLeadId={id}
              generic_documents={privateCompanyLead.generic_documents}
              control={control}
              disabled
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={privateCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[privateCompanyLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
              Buttons={() => (
                <>
                  {
                    isInQuotingClientReview && !passedEveryRequirement ?
                      <ResentContactEmailModal contacts={privateCompanyLead.contacts || []} /> :
                      <Button
                        type="button"
                        disabled={advancePrivateCompanyLeadIsPending || isInQuotingClientReview || !passedEveryRequirement}
                        onClick={() => advancePrivateCompanyLead()}
                        isLoading={advancePrivateCompanyLeadIsPending}
                      >
                        {(isInQuotingManagementReview || isInQuotingClientReview) ? 'Enviar para Contatos' : 'Enviar para análise do Gestor'}
                      </Button>
                  }
                </>
              )}
            />
            <ChangeHistory logs={privateCompanyLead?.logs || []} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default LeadQuotingManagementReviewForm;
